// i18n configuration for ptc-tax-lifecycle.
//
// Fleet i18n standard (2026-10-21): ptc-docs/coordination/plans/2026-10-21-fleet-i18n-standard.md.
// "No default" ruling (Christian, 2026-10-21): NO language is ever assigned to a staff member —
// they switch it themselves with the header toggle. The choice persists twice: per BROWSER
// (this detector's localStorage cache, so the page paints in it before sign-in resolves) and per
// PERSON (api/user-preferences, so it follows them to another machine — see LanguageToggle.tsx,
// which applies the server value ONCE after it arrives and never again overrides a live choice).
//
// Scope fence on what gets translated (same as statetax's, the fleet's reference implementation):
// - YES: UI chrome (nav labels, page titles, action buttons, empty/error messages)
// - NO:  Dataverse-sourced content (client names, notes, filing data) — source-of-truth data, not
//        UI chrome.
// - NO:  formatted numbers/dates — Intl/date utilities handle locale, not translation keys.
//
// Key naming: hierarchical, dot-delimited, grouped by page or surface. Examples:
//   nav.dashboard, common.cancel, shell.feedback.send, <page>Page.title
//
// Add a new string: drop a key into both locales/en.json and locales/es.json, then
// `const { t } = useTranslation(); t('your.new.key')`.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import es from './locales/es.json';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es'],
    interpolation: { escapeValue: false }, // React escapes already.
    detection: {
      // Priority: explicit user choice (localStorage) > browser preference > fallback. The
      // per-person server value (api/user-preferences) is applied on top of this once it arrives
      // -- see LanguageToggle.tsx -- so this detector only owns the FIRST PAINT, before that GET
      // resolves.
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ptc-tax-lifecycle-lang',
    },
    returnEmptyString: false, // Empty string in a locale = fall back to fallbackLng.
  });

// <html lang> follows the UI language, so a screen reader pronounces Spanish as Spanish.
const syncDocumentLang = (lng: string | undefined) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng?.startsWith('es') ? 'es' : 'en';
};
i18n.on('languageChanged', syncDocumentLang);
syncDocumentLang(i18n.resolvedLanguage ?? i18n.language);

export default i18n;
